import React, { useState, useEffect, useRef } from 'react';

// Normalize any date-like value to YYYY-MM-DD without timezone shifts
const normalizeIsoDate = (value) => {
  if (!value) return '';
  if (value instanceof Date) {
    const y = value.getUTCFullYear();
    const m = value.getUTCMonth() + 1;
    const d = value.getUTCDate();
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    return `${y}-${pad(m)}-${pad(d)}`;
  }
  if (typeof value === 'string') {
    // Accept ISO with time or bare date
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return '';
};

// Convert ISO date (YYYY-MM-DD) to display format (DD.MM.YYYY)
const isoToDisplay = (isoDate) => {
  const normalized = normalizeIsoDate(isoDate);
  if (!normalized) return '';
  const [year, month, day] = normalized.split('-');
  return `${day}.${month}.${year}`;
};

// Convert display format (DD.MM.YYYY) to ISO (YYYY-MM-DD)
const displayToIso = (displayDate) => {
  if (!displayDate) return '';
  const match = displayDate.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return '';
};

// Validate date string in DD.MM.YYYY format
const isValidDate = (dateStr) => {
  const match = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return false;

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Check valid day for month
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day > daysInMonth) return false;

  return true;
};

function DateInput({ value, onChange, required, placeholder = 'DD.MM.YYYY', showTodayButton = false, ...props }) {
  const [displayValue, setDisplayValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const datePickerRef = useRef(null);

  // Update display value when prop value changes
  useEffect(() => {
    const normalized = normalizeIsoDate(value);
    setDisplayValue(isoToDisplay(normalized));
  }, [value]);

  // Handle date picker change
  const handleDatePickerChange = (e) => {
    const isoValue = normalizeIsoDate(e.target.value);
    if (isoValue) {
      setDisplayValue(isoToDisplay(isoValue));
      setIsValid(true);
      onChange(isoValue);
    }
  };

  // Open date picker
  const openDatePicker = () => {
    if (datePickerRef.current) {
      datePickerRef.current.showPicker();
    }
  };

  // Set today's date
  const setToday = () => {
    const now = new Date();
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    setDisplayValue(isoToDisplay(today));
    setIsValid(true);
    onChange(today);
  };

  const handleChange = (e) => {
    let input = e.target.value;

    // Allow only digits and dots
    input = input.replace(/[^\d.]/g, '');

    // Auto-add dots after day and month
    if (input.length === 2 && !input.includes('.')) {
      input += '.';
    } else if (input.length === 5 && input.charAt(2) === '.' && input.charAt(5) !== '.') {
      input = input.substring(0, 5) + '.';
    }

    // Limit length
    if (input.length > 10) {
      input = input.substring(0, 10);
    }

    setDisplayValue(input);

    // Validate and convert to ISO
    if (input === '') {
      setIsValid(true);
      onChange('');
    } else if (input.length === 10 && isValidDate(input)) {
      setIsValid(true);
      onChange(displayToIso(input));
    } else if (input.length === 10) {
      setIsValid(false);
    } else {
      setIsValid(true); // Still typing
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    let pasted = e.clipboardData.getData('text').trim();

    // Try to parse various formats
    let parsed = '';

    // DD.MM.YYYY
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(pasted)) {
      parsed = pasted;
    }
    // DD/MM/YYYY
    else if (/^\d{2}\/\d{2}\/\d{4}$/.test(pasted)) {
      parsed = pasted.replace(/\//g, '.');
    }
    // DD-MM-YYYY
    else if (/^\d{2}-\d{2}-\d{4}$/.test(pasted)) {
      parsed = pasted.replace(/-/g, '.');
    }
    // YYYY-MM-DD (ISO)
    else if (/^\d{4}-\d{2}-\d{2}$/.test(pasted)) {
      const [y, m, d] = pasted.split('-');
      parsed = `${d}.${m}.${y}`;
    }
    // DDMMYYYY (no separators)
    else if (/^\d{8}$/.test(pasted)) {
      parsed = `${pasted.substr(0, 2)}.${pasted.substr(2, 2)}.${pasted.substr(4, 4)}`;
    }

    if (parsed && isValidDate(parsed)) {
      setDisplayValue(parsed);
      setIsValid(true);
      onChange(displayToIso(parsed));
    } else {
      // Just set the pasted value and let user correct it
      setDisplayValue(pasted.substring(0, 10));
      setIsValid(false);
    }
  };

  const handleBlur = () => {
    // On blur, validate and update
    if (displayValue && displayValue.length === 10) {
      if (isValidDate(displayValue)) {
        setIsValid(true);
        onChange(displayToIso(displayValue));
      } else {
        setIsValid(false);
      }
    } else if (displayValue && displayValue.length > 0) {
      setIsValid(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flex: 1 }}>
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          style={{
            borderColor: !isValid ? '#e74c3c' : undefined,
            paddingRight: '30px',
            width: '100%',
            ...props.style
          }}
          {...props}
        />
        <input
          ref={datePickerRef}
          type="date"
          value={normalizeIsoDate(value) || ''}
          onChange={handleDatePickerChange}
          style={{
            position: 'absolute',
            right: '5px',
            width: '20px',
            height: '20px',
            opacity: 0,
            cursor: 'pointer'
          }}
          tabIndex={-1}
        />
        <span
          onClick={openDatePicker}
          style={{
            position: 'absolute',
            right: '8px',
            cursor: 'pointer',
            color: '#666',
            fontSize: '14px',
            pointerEvents: 'auto'
          }}
          title="Open calendar"
        >
          📅
        </span>
      </div>
      {showTodayButton && (
        <button
          type="button"
          onClick={setToday}
          style={{
            padding: '4px 8px',
            fontSize: '0.85em',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
          title="Set today's date"
        >
          Today
        </button>
      )}
    </div>
  );
}

export default DateInput;
